import React, { useState, useEffect } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useEasyCheck } from './context/EasyCheckContext';
import { canAccess } from './utils/planPermissions';
import { companyPreferencesService } from './services/settings/companyPreferencesService';
import { usePermissions } from './hooks/usePermissions';
import EasyCheckAppBar from './components/common/Layout/EasyCheckAppBar';
import ProtectedRoute from './components/common/Navigation/ProtectedRoute';

import LoginPage from './components/pages/LoginPage';
import OtpVerificationForm from './components/features/Auth/OtpVerificationForm';
import SignupPage from './components/pages/SignupPage';
import EmailVerificationPage from './components/pages/EmailVerificationPage';
import PaymentSuccessPage from './components/pages/PaymentSuccessPage';
import PaymentCancelledPage from './components/pages/PaymentCancelledPage';
import DashboardPage from './components/pages/DashboardPage';
import BankSetupPage from './components/features/Banks/BankSetupPage';
import BeneficiarySetupPage from './components/features/Beneficiaries/BeneficiarySetupPage';
import ChequePrintingForm from './components/features/ChequePrinting/ChequePrintingForm';
import ReportsPage from './components/pages/ReportsPage';
import SettingsPage from './components/features/Settings/SettingsPage';
import PreferencesPage from './components/features/Settings/PreferencesPage';
import VoucherDesignPage from './components/features/Settings/VoucherDesignPage';
import ZohoSetupPage from './components/features/Settings/ZohoSetupPage';
import UserManagementPage from './components/features/UserManagement/UserManagementPage';
import ResetPasswordPage from './components/pages/ResetPasswordPage';
import BulkChequesPage from './components/features/BulkCheques/BulkChequesPage';
import PDCCalendarPage from './components/pages/PDCCalendarPage';
import ChequeCollectionPage from './components/features/ChequeCollection/ChequeCollectionPage';

// Super Admin imports
import SuperAdminLogin from './components/features/SuperAdmin/SuperAdminLogin';
import SuperAdminOtpVerify from './components/features/SuperAdmin/SuperAdminOtpVerify';
import SuperAdminLayout from './components/features/SuperAdmin/SuperAdminLayout';
import SuperAdminDashboard from './components/features/SuperAdmin/SuperAdminDashboard';
import SuperAdminProtectedRoute from './components/features/SuperAdmin/SuperAdminProtectedRoute';
import CompanyListPage from './components/features/SuperAdmin/CompanyListPage';
import InvoiceListPage from './components/features/SuperAdmin/InvoiceListPage';
import PaymentCodesPage from './components/features/SuperAdmin/PaymentCodesPage';
import SignupAttemptsPage from './components/features/SuperAdmin/SignupAttemptsPage';
import UsageStatsPage from './components/features/SuperAdmin/UsageStatsPage';
import TemplateLibraryPage from './components/features/SuperAdmin/TemplateLibraryPage';

// Mobile imports
import MobileLogin from './components/features/Mobile/MobileLogin';
import MobileOtpVerify from './components/features/Mobile/MobileOtpVerify';
import MobileLayout from './components/features/Mobile/MobileLayout';
import MobileDashboard from './components/features/Mobile/MobileDashboard';
import MobileChequeForm from './components/features/Mobile/MobileChequeForm';
import MobileChequeList from './components/features/Mobile/MobileChequeList';
import MobileChequeDetail from './components/features/Mobile/MobileChequeDetail';
import MobileProtectedRoute from './components/features/Mobile/MobileProtectedRoute';
import MobileBeneficiaryList from './components/features/Mobile/MobileBeneficiaryList';
import MobileBeneficiaryDetail from './components/features/Mobile/MobileBeneficiaryDetail';
import MobileBeneficiaryForm from './components/features/Mobile/MobileBeneficiaryForm';
import MobilePDCCalendar from './components/features/Mobile/MobilePDCCalendar';
import MobileChequeCollections from './components/features/Mobile/MobileChequeCollections';
import MobileReportsPage from './components/features/Mobile/MobileReportsPage';
import { 
  Box, 
  Drawer, 
  useMediaQuery, 
  useTheme,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
  Typography,
  Toolbar,
  IconButton,
  Tooltip
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  AccountBalance as BankIcon,
  Print as PrintIcon,
  Assessment as ReportsIcon,
  Person as BeneficiaryIcon,
  Menu as MenuIcon,
  DynamicFeed as BulkChequesIcon,
  EventNote as CalendarIcon,
  LocalShipping as CollectionIcon,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { styled } from '@mui/material/styles';

// Logo styled components
const LogoText = styled(Typography)(({ theme }) => ({
  fontSize: '1.25rem',
  fontWeight: 700,
  fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
  textDecoration: 'none',
}));

const LogoEasy = styled('span')(({ theme }) => ({
  color: '#0f172a', // Dark gray/black
}));

const LogoCheck = styled('span')(({ theme }) => ({
  color: '#2563eb', // Blue
}));

const drawerWidth = 280;
const collapsedDrawerWidth = 80;

// Main Layout component for authenticated pages
const MainLayout = ({ children }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useEasyCheck();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [drawerOpen, setDrawerOpen] = useState(!isMobile);
  const [drawerCollapsed, setDrawerCollapsed] = useState(false);

  const handleDrawerToggle = () => {
    if (isMobile) {
      setDrawerOpen(!drawerOpen);
    } else {
      setDrawerCollapsed(!drawerCollapsed);
    }
  };

  const planId = user?.planId || 'business';

  const [companyPrefs, setCompanyPrefs] = useState(null);
  useEffect(() => {
    companyPreferencesService.getPreferences().then((result) => {
      if (result.success) setCompanyPrefs(result.data);
    });
  }, []);

  const { canAccess: canAccessUserModule } = usePermissions();

  const menuItems = [
    { id: 'dashboard', text: 'Dashboard', icon: <DashboardIcon />, path: '/' },
    { id: 'print-cheque', text: 'Print Cheque', icon: <PrintIcon />, path: '/print-cheque' },
    { id: 'bulk-cheques', text: 'Bulk Cheques', icon: <BulkChequesIcon />, path: '/bulk-cheques', requiredFeature: 'bulkCheques' },
    { id: 'pdc-calendar', text: 'PDC Calendar', icon: <CalendarIcon />, path: '/pdc-calendar' },
    { id: 'cheque-collection', text: 'Cheque Collection', icon: <CollectionIcon />, path: '/cheque-collection', requiresPref: 'enableChequeCollection' },
    { id: 'reports', text: 'Reports', icon: <ReportsIcon />, path: '/reports' },
    { id: 'beneficiary-setup', text: 'Beneficiary Master', icon: <BeneficiaryIcon />, path: '/beneficiary-setup' },
    { id: 'bank-setup', text: 'Bank Setup', icon: <BankIcon />, path: '/bank-setup' },
  ].filter(item => {
    if (item.requiredFeature && !canAccess(planId, item.requiredFeature)) return false;
    if (item.requiresPref && !companyPrefs?.[item.requiresPref]) return false;
    if (item.id !== 'dashboard' && !canAccessUserModule(item.id)) return false;
    return true;
  });

  const handleMenuItemClick = (item) => {
    navigate(item.path);
    if (isMobile) {
      setDrawerOpen(false);
    }
  };

  const drawer = (
    <div style={{ 
      backgroundColor: '#F7F9FC', 
      backgroundImage: 'none',
      height: '100%',
      width: '100%'
    }}>
      {/* Logo Section */}
      <Toolbar sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center',
        px: 2,
        minHeight: '64px !important',
        backgroundColor: '#F7F9FC',
        backgroundImage: 'none',
      }}>
        {drawerCollapsed ? (
          <LogoText sx={{ fontSize: '1.5rem' }}>
            <LogoEasy>E</LogoEasy>
            <LogoCheck>C</LogoCheck>
          </LogoText>
        ) : (
          <LogoText sx={{ fontSize: '1.5rem' }}>
            <LogoEasy>Easy</LogoEasy>
            <LogoCheck>Check</LogoCheck>
          </LogoText>
        )}
      </Toolbar>
      <List sx={{ 
        backgroundColor: '#F7F9FC',
        backgroundImage: 'none',
      }}>
        {menuItems.map((item) => (
          <ListItem key={item.id} disablePadding>
            <ListItemButton
              selected={location.pathname === item.path || (item.path !== '/' && location.pathname.startsWith(`${item.path}/`))}
              onClick={() => handleMenuItemClick(item)}
              sx={{
                mx: 1,
                borderRadius: 2,
                py: 1.5,
                justifyContent: drawerCollapsed ? 'center' : 'flex-start',
                '&.Mui-selected': {
                  backgroundColor: 'primary.main',
                  color: 'white',
                  '&:hover': {
                    backgroundColor: 'primary.dark',
                  },
                  '& .MuiListItemIcon-root': {
                    color: 'white',
                  },
                  '& .MuiListItemText-primary': {
                    color: 'white',
                    fontWeight: 500,
                  },
                },
                '&:hover': {
                  backgroundColor: 'primary.light',
                  color: 'white',
                  '& .MuiListItemIcon-root': {
                    color: 'white',
                  },
                  '& .MuiListItemText-primary': {
                    color: 'white',
                  },
                },
              }}
            >
              <ListItemIcon sx={{ 
                minWidth: drawerCollapsed ? 0 : 40,
                justifyContent: 'center'
              }}>
                {item.icon}
              </ListItemIcon>
              {!drawerCollapsed && (
                <ListItemText 
                  primary={item.text} 
                  primaryTypographyProps={{
                    fontSize: '0.875rem',
                    fontWeight: 500,
                  }}
                />
              )}
            </ListItemButton>
          </ListItem>
        ))}
      </List>
    </div>
  );

  const currentDrawerWidth = drawerCollapsed ? collapsedDrawerWidth : drawerWidth;

  return (
    <Box sx={{ display: 'flex' }}>
      {/* Sidebar Navigation */}
      <Box
        component="nav"
        sx={{ width: { md: currentDrawerWidth }, flexShrink: { md: 0 } }}
      >
        <Drawer
          variant={isMobile ? 'temporary' : 'permanent'}
          open={drawerOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true, // Better mobile performance
          }}
          sx={{
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: isMobile ? drawerWidth : currentDrawerWidth,
              backgroundColor: '#F7F9FC !important', // Force solid background to override global gradient
              backgroundImage: 'none !important', // Remove any background image/gradient
              borderRight: '1px solid',
              borderColor: 'divider',
              transition: 'width 0.3s ease',
            },
          }}
        >
          {drawer}
        </Drawer>
      </Box>

      {/* Main Content Area */}
      <Box 
        sx={{ 
          flexGrow: 1, 
          width: { md: `calc(100% - ${currentDrawerWidth}px)` },
          transition: 'width 0.3s ease',
          height: '100vh', // Fixed height
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden', // Prevent overflow at this level
        }}
      >
        {/* App Bar - only covers main content area */}
        <EasyCheckAppBar onMenuToggle={handleDrawerToggle} />
        
        {/* Main Content */}
        <Box 
          component="main" 
          sx={{ 
            height: 'calc(100vh - 64px)', // Subtract app bar height
            overflow: 'hidden',
          }}
        >
          {children}
        </Box>
      </Box>
    </Box>
  );
};

function App() {
  const { user } = useEasyCheck();

  return (
    <>
      <Routes>
        {/* Public routes */}
        <Route path="/login" element={user ? <Navigate to="/" /> : <LoginPage />} />
        <Route path="/verify-otp" element={user ? <Navigate to="/" /> : <OtpVerificationForm />} />
        <Route path="/signup" element={user ? <Navigate to="/" /> : <SignupPage />} />
        <Route path="/verify-email" element={user ? <Navigate to="/" /> : <EmailVerificationPage />} />
        <Route path="/reset-password" element={user ? <Navigate to="/" /> : <ResetPasswordPage />} />
        
        {/* Redirect routes to handle API endpoint URLs from emails */}
        <Route path="/api/usersignup/verifyemail" element={<EmailVerificationRedirect />} />
        <Route path="/api/usersignup/resetpassword" element={<PasswordResetRedirect />} />
        
        <Route path="/signup/payment-success" element={<PaymentSuccessPage />} />
        <Route path="/signup/payment-cancelled" element={<PaymentCancelledPage />} />
        
        {/* Protected routes with navigation */}
        <Route 
          path="/" 
          element={
            <ProtectedRoute>
              <MainLayout>
                <DashboardPage />
              </MainLayout>
            </ProtectedRoute>
          } 
        />
        <Route
          path="/print-cheque"
          element={
            <ProtectedRoute requiredPermission="print-cheque">
              <MainLayout>
                <ChequePrintingForm />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/bulk-cheques"
          element={
            <ProtectedRoute requiredPermission="bulk-cheques">
              <MainLayout>
                <BulkChequesPage />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/pdc-calendar"
          element={
            <ProtectedRoute requiredPermission="pdc-calendar">
              <MainLayout>
                <PDCCalendarPage />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/cheque-collection"
          element={
            <ProtectedRoute requiredPermission="cheque-collection">
              <MainLayout>
                <ChequeCollectionPage />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/reports"
          element={
            <ProtectedRoute requiredPermission="reports">
              <MainLayout>
                <ReportsPage />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/bank-setup"
          element={
            <ProtectedRoute requiredPermission="bank-setup">
              <MainLayout>
                <BankSetupPage />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/beneficiary-setup"
          element={
            <ProtectedRoute requiredPermission="beneficiary-setup">
              <MainLayout>
                <BeneficiarySetupPage />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route 
          path="/settings" 
          element={
            <ProtectedRoute>
              <MainLayout>
                <SettingsPage />
              </MainLayout>
            </ProtectedRoute>
          } 
        />
        <Route
          path="/user-management"
          element={
            <ProtectedRoute>
              <MainLayout>
                <UserManagementPage />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/preferences"
          element={
            <ProtectedRoute>
              <MainLayout>
                <PreferencesPage />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/preferences/voucher-design"
          element={
            <ProtectedRoute>
              <MainLayout>
                <VoucherDesignPage />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/preferences/zoho"
          element={
            <ProtectedRoute>
              <MainLayout>
                <ZohoSetupPage />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        {/* Super Admin routes */}
        <Route path="/super-admin/login" element={<SuperAdminLogin />} />
        <Route path="/super-admin/verify-otp" element={<SuperAdminOtpVerify />} />
        <Route
          path="/super-admin"
          element={
            <SuperAdminProtectedRoute>
              <SuperAdminLayout>
                <SuperAdminDashboard />
              </SuperAdminLayout>
            </SuperAdminProtectedRoute>
          }
        />
        <Route
          path="/super-admin/companies"
          element={
            <SuperAdminProtectedRoute>
              <SuperAdminLayout>
                <CompanyListPage />
              </SuperAdminLayout>
            </SuperAdminProtectedRoute>
          }
        />
        <Route
          path="/super-admin/invoices"
          element={
            <SuperAdminProtectedRoute>
              <SuperAdminLayout>
                <InvoiceListPage />
              </SuperAdminLayout>
            </SuperAdminProtectedRoute>
          }
        />
        <Route
          path="/super-admin/payment-codes"
          element={
            <SuperAdminProtectedRoute>
              <SuperAdminLayout>
                <PaymentCodesPage />
              </SuperAdminLayout>
            </SuperAdminProtectedRoute>
          }
        />
        <Route
          path="/super-admin/signup-attempts"
          element={
            <SuperAdminProtectedRoute>
              <SuperAdminLayout>
                <SignupAttemptsPage />
              </SuperAdminLayout>
            </SuperAdminProtectedRoute>
          }
        />
        <Route
          path="/super-admin/usage"
          element={
            <SuperAdminProtectedRoute>
              <SuperAdminLayout>
                <UsageStatsPage />
              </SuperAdminLayout>
            </SuperAdminProtectedRoute>
          }
        />
        <Route
          path="/super-admin/template-library"
          element={
            <SuperAdminProtectedRoute>
              <SuperAdminLayout>
                <TemplateLibraryPage />
              </SuperAdminLayout>
            </SuperAdminProtectedRoute>
          }
        />

        {/* Mobile routes */}
        <Route path="/mobile/login" element={<MobileLogin />} />
        <Route path="/mobile/verify-otp" element={<MobileOtpVerify />} />
        <Route
          path="/mobile"
          element={
            <MobileProtectedRoute>
              <MobileLayout>
                <MobileDashboard />
              </MobileLayout>
            </MobileProtectedRoute>
          }
        />
        <Route
          path="/mobile/new-cheque"
          element={
            <MobileProtectedRoute>
              <MobileLayout>
                <MobileChequeForm />
              </MobileLayout>
            </MobileProtectedRoute>
          }
        />
        <Route
          path="/mobile/cheques"
          element={
            <MobileProtectedRoute>
              <MobileLayout>
                <MobileChequeList />
              </MobileLayout>
            </MobileProtectedRoute>
          }
        />
        <Route
          path="/mobile/cheques/:id"
          element={
            <MobileProtectedRoute>
              <MobileLayout>
                <MobileChequeDetail />
              </MobileLayout>
            </MobileProtectedRoute>
          }
        />
        <Route
          path="/mobile/pdc-calendar"
          element={
            <MobileProtectedRoute>
              <MobileLayout>
                <MobilePDCCalendar />
              </MobileLayout>
            </MobileProtectedRoute>
          }
        />
        <Route
          path="/mobile/collections"
          element={
            <MobileProtectedRoute>
              <MobileLayout>
                <MobileChequeCollections />
              </MobileLayout>
            </MobileProtectedRoute>
          }
        />
        <Route
          path="/mobile/reports"
          element={
            <MobileProtectedRoute>
              <MobileLayout>
                <MobileReportsPage />
              </MobileLayout>
            </MobileProtectedRoute>
          }
        />
        <Route
          path="/mobile/beneficiaries"
          element={
            <MobileProtectedRoute>
              <MobileLayout>
                <MobileBeneficiaryList />
              </MobileLayout>
            </MobileProtectedRoute>
          }
        />
        <Route
          path="/mobile/beneficiaries/new"
          element={
            <MobileProtectedRoute>
              <MobileLayout>
                <MobileBeneficiaryForm />
              </MobileLayout>
            </MobileProtectedRoute>
          }
        />
        <Route
          path="/mobile/beneficiaries/:id"
          element={
            <MobileProtectedRoute>
              <MobileLayout>
                <MobileBeneficiaryDetail />
              </MobileLayout>
            </MobileProtectedRoute>
          }
        />
        <Route
          path="/mobile/beneficiaries/:id/edit"
          element={
            <MobileProtectedRoute>
              <MobileLayout>
                <MobileBeneficiaryForm />
              </MobileLayout>
            </MobileProtectedRoute>
          }
        />

        <Route path="*" element={<Navigate to="/" />} />
      </Routes>
    </>
  );
}

// Redirect components to handle API endpoint URLs from emails
const EmailVerificationRedirect = () => {
  React.useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const token = urlParams.get('token');
    const email = urlParams.get('email');
    
    if (token && email) {
      // Redirect to the proper frontend page with the same parameters
      window.location.replace(`/verify-email?token=${encodeURIComponent(token)}&email=${encodeURIComponent(email)}`);
    } else {
      // If no parameters, redirect to signup
      window.location.replace('/signup');
    }
  }, []);

  // Show loading while redirecting
  return (
    <Box sx={{ 
      display: 'flex', 
      justifyContent: 'center', 
      alignItems: 'center', 
      height: '100vh',
      flexDirection: 'column',
      bgcolor: '#f5f5f5'
    }}>
      <Typography variant="h6" sx={{ mb: 2, color: '#666' }}>
        Redirecting to email verification...
      </Typography>
      <Box sx={{ 
        width: 24, 
        height: 24, 
        border: '3px solid #e0e0e0', 
        borderTop: '3px solid #2563eb',
        borderRadius: '50%',
        animation: 'spin 1s linear infinite'
      }} />
      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}
      </style>
    </Box>
  );
};

const PasswordResetRedirect = () => {
  React.useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const token = urlParams.get('token');
    const email = urlParams.get('email');
    
    if (token && email) {
      // Redirect to the proper frontend page with the same parameters
      window.location.replace(`/reset-password?token=${encodeURIComponent(token)}&email=${encodeURIComponent(email)}`);
    } else {
      // If no parameters, redirect to login
      window.location.replace('/login');
    }
  }, []);

  // Show loading while redirecting
  return (
    <Box sx={{ 
      display: 'flex', 
      justifyContent: 'center', 
      alignItems: 'center', 
      height: '100vh',
      flexDirection: 'column',
      bgcolor: '#f5f5f5'
    }}>
      <Typography variant="h6" sx={{ mb: 2, color: '#666' }}>
        Redirecting to password reset...
      </Typography>
      <Box sx={{ 
        width: 24, 
        height: 24, 
        border: '3px solid #e0e0e0', 
        borderTop: '3px solid #2563eb',
        borderRadius: '50%',
        animation: 'spin 1s linear infinite'
      }} />
      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}
      </style>
    </Box>
  );
};

export default App;
